import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Select, Space, Tag, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';

import { JST_TIMEZONE } from './analysisConfig';
import { parseMeasuredAt } from './dateUtils';
import {
  MANUAL_TRIPLEG_MODE_OPTIONS,
  SEMANTIC_TYPE_OPTIONS,
  SemanticTypeIcon,
  TriplegModeIcon,
  getSemanticTypeLabel,
  getTriplegModeLabel,
} from './timelineFormatters';
import type {
  DerivedLocation,
  DerivedStaypoint,
  DerivedTimelineItem,
  DerivedTimelineTarget,
  DerivedTripleg,
  LocationAnalysisTimelineActions,
  POICandidate,
  POIEnrichment,
} from './timelineTypes';
import './LocationAnalysisTimeline.css';

const { Text } = Typography;

const POI_INCREMENTAL_SEARCH_DEBOUNCE_MS = 600;
const POI_INCREMENTAL_SEARCH_MIN_QUERY_LENGTH = 2;
const timelineDateTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: JST_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatTimelineDateTime = (value?: string | null) => {
  const parsed = parseMeasuredAt(value);
  return parsed ? timelineDateTimeFormatter.format(parsed) : '-';
};

const getDurationMinutes = (start?: string | null, end?: string | null) => {
  const startedAt = parseMeasuredAt(start);
  const endedAt = parseMeasuredAt(end);
  if (!startedAt || !endedAt) {
    return null;
  }
  const diffMs = endedAt.getTime() - startedAt.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) {
    return null;
  }
  return Math.max(1, Math.round(diffMs / 60000));
};

const formatTimelineDuration = (start?: string | null, end?: string | null) => {
  const totalMinutes = getDurationMinutes(start, end);
  if (totalMinutes == null) {
    return '-';
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}時間${minutes}分` : `${minutes}分`;
};

const formatTimelineDistance = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }
  return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
};

const formatAverageSpeed = (distanceM?: number | null, start?: string | null, end?: string | null) => {
  if (typeof distanceM !== 'number' || !Number.isFinite(distanceM)) {
    return '-';
  }
  const durationMinutes = getDurationMinutes(start, end);
  if (!durationMinutes) {
    return '-';
  }
  const speedKmh = (distanceM / 1000) / (durationMinutes / 60);
  return `${Math.round(speedKmh)}km/h`;
};

type LocationAnalysisTimelineProps = LocationAnalysisTimelineActions & {
  editable?: boolean;
  items: DerivedTimelineItem[];
  locations: DerivedLocation[];
  locationNameMap: Map<string, string>;
  locationPoiMap: Map<string, POIEnrichment>;
  poiCandidatesByLocationId: Map<string, POICandidate[]>;
  searchingPoiLocationId: string | null;
  linkingPoiLocationId: string | null;
  selectedTarget: DerivedTimelineTarget | null;
  formatDateTime: (value?: string | null) => string;
  formatDistance: (value?: number | null) => string;
};

export const isSameTimelineTarget = (left: DerivedTimelineTarget | null, right: DerivedTimelineTarget) =>
  left?.type === right.type && left.id === right.id;

const LocationAnalysisTimeline: React.FC<LocationAnalysisTimelineProps> = ({
  editable = true,
  items,
  locations,
  locationNameMap,
  locationPoiMap,
  poiCandidatesByLocationId,
  searchingPoiLocationId,
  linkingPoiLocationId,
  selectedTarget,
  onSelect,
  onRenameLocation,
  onUpdateLocationSemanticType,
  onOverrideTriplegMode,
  onDeleteTripleg,
  onReassignStaypointLocation,
  onSearchLocationPoi,
  onLinkLocationPoi,
}) => {
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [draftLocationLabel, setDraftLocationLabel] = useState('');
  const [savingLocationId, setSavingLocationId] = useState<string | null>(null);
  const [poiSearchQueryByLocationId, setPoiSearchQueryByLocationId] = useState<Map<string, string>>(new Map());
  const [reassignTarget, setReassignTarget] = useState<{ staypoint: DerivedStaypoint; displayIndex: number } | null>(null);
  const [reassignMode, setReassignMode] = useState<'assign' | 'create_location' | 'clear'>('assign');
  const [reassignLocationId, setReassignLocationId] = useState<string | undefined>(undefined);
  const [newLocationLabel, setNewLocationLabel] = useState('');
  const [newLocationSemanticType, setNewLocationSemanticType] = useState('unknown');
  const [reassignSemanticType, setReassignSemanticType] = useState('unknown');
  const [savingReassign, setSavingReassign] = useState(false);
  const [editingTripleg, setEditingTripleg] = useState<{ tripleg: DerivedTripleg; displayIndex: number } | null>(null);
  const [triplegModeDraft, setTriplegModeDraft] = useState('walk');
  const [savingTriplegMode, setSavingTriplegMode] = useState(false);
  const [querySearchingPoiLocationId, setQuerySearchingPoiLocationId] = useState<string | null>(null);
  const lastIncrementalPoiQueryRef = useRef<Map<string, string>>(new Map());
  const incrementalPoiSearchAllowedRef = useRef<Map<string, boolean>>(new Map());
  const poiQuerySearchRequestIdByLocationRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (editable) {
      return;
    }
    setEditingLocationId(null);
    setDraftLocationLabel('');
    setPoiSearchQueryByLocationId(new Map());
    setReassignTarget(null);
    setEditingTripleg(null);
    setQuerySearchingPoiLocationId(null);
    lastIncrementalPoiQueryRef.current.clear();
    incrementalPoiSearchAllowedRef.current.clear();
    poiQuerySearchRequestIdByLocationRef.current.clear();
  }, [editable]);

  const runLocationPoiQuerySearch = useCallback(async (
    locationId: string,
    query: string,
    options?: { silent?: boolean }
  ) => {
    const requestId = (poiQuerySearchRequestIdByLocationRef.current.get(locationId) || 0) + 1;
    poiQuerySearchRequestIdByLocationRef.current.set(locationId, requestId);
    setQuerySearchingPoiLocationId(locationId);
    try {
      await onSearchLocationPoi(locationId, query, options);
    } finally {
      if (poiQuerySearchRequestIdByLocationRef.current.get(locationId) === requestId) {
        setQuerySearchingPoiLocationId(null);
      }
    }
  }, [onSearchLocationPoi]);

  useEffect(() => {
    if (!editable) {
      return undefined;
    }

    const searchableEntries = Array.from(poiSearchQueryByLocationId.entries())
      .map(([locationId, query]) => [locationId, query.trim()] as const)
      .filter(
        ([locationId, query]) =>
          poiCandidatesByLocationId.has(locationId) &&
          incrementalPoiSearchAllowedRef.current.get(locationId) === true &&
          query.length >= POI_INCREMENTAL_SEARCH_MIN_QUERY_LENGTH
      );

    Array.from(lastIncrementalPoiQueryRef.current.keys()).forEach((locationId) => {
      const query = poiSearchQueryByLocationId.get(locationId)?.trim() || '';
      if (!poiCandidatesByLocationId.has(locationId) || query.length < POI_INCREMENTAL_SEARCH_MIN_QUERY_LENGTH) {
        lastIncrementalPoiQueryRef.current.delete(locationId);
        incrementalPoiSearchAllowedRef.current.delete(locationId);
      }
    });

    if (searchableEntries.length === 0) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      searchableEntries.forEach(([locationId, query]) => {
        if (lastIncrementalPoiQueryRef.current.get(locationId) === query) {
          return;
        }
        lastIncrementalPoiQueryRef.current.set(locationId, query);
        void runLocationPoiQuerySearch(locationId, query, { silent: true }).catch(() => undefined);
      });
    }, POI_INCREMENTAL_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timerId);
  }, [editable, poiCandidatesByLocationId, poiSearchQueryByLocationId, runLocationPoiQuerySearch]);

  if (items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="タイムライン対象がありません" />;
  }

  const startEditingLocation = (event: React.MouseEvent, locationId: string, currentLabel: string | null) => {
    event.stopPropagation();
    setEditingLocationId(locationId);
    setDraftLocationLabel(currentLabel || '');
  };

  const cancelEditingLocation = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setEditingLocationId(null);
    setDraftLocationLabel('');
  };

  const saveEditingLocation = async (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!editingLocationId) {
      return;
    }
    setSavingLocationId(editingLocationId);
    try {
      await onRenameLocation(editingLocationId, draftLocationLabel);
      setEditingLocationId(null);
      setDraftLocationLabel('');
    } finally {
      setSavingLocationId(null);
    }
  };

  const handleTimelineKeyDown = (event: React.KeyboardEvent, target: DerivedTimelineTarget) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(target);
    }
  };

  const openReassignModal = (event: React.MouseEvent, staypoint: DerivedStaypoint, displayIndex: number) => {
    event.stopPropagation();
    setReassignTarget({ staypoint, displayIndex });
    setReassignMode('assign');
    setReassignLocationId(staypoint.effective_location_id || undefined);
    setNewLocationLabel('');
    const currentLocation = locations.find((location) => location.id === staypoint.effective_location_id);
    setNewLocationSemanticType(currentLocation?.semantic_type || 'unknown');
    setReassignSemanticType(currentLocation?.semantic_type || 'unknown');
  };

  const closeReassignModal = () => {
    if (savingReassign) {
      return;
    }
    setReassignTarget(null);
  };

  const saveReassign = async () => {
    if (!reassignTarget) {
      return;
    }
    if (reassignMode === 'assign' && !reassignLocationId) {
      message.warning('割当先 location を選択してください');
      return;
    }
    setSavingReassign(true);
    try {
      if (reassignMode === 'assign') {
        await onReassignStaypointLocation(reassignTarget.staypoint.id, {
          action: 'assign',
          locationId: reassignLocationId!,
        });
        const selectedLocation = locations.find((location) => location.id === reassignLocationId);
        if (selectedLocation && selectedLocation.semantic_type !== reassignSemanticType) {
          await onUpdateLocationSemanticType(selectedLocation.id, reassignSemanticType);
        }
      } else if (reassignMode === 'create_location') {
        await onReassignStaypointLocation(reassignTarget.staypoint.id, {
          action: 'create_location',
          label: newLocationLabel.trim() || null,
          semanticType: newLocationSemanticType,
        });
      } else {
        await onReassignStaypointLocation(reassignTarget.staypoint.id, { action: 'clear' });
      }
      setReassignTarget(null);
    } finally {
      setSavingReassign(false);
    }
  };

  const openTriplegModeModal = (event: React.MouseEvent, tripleg: DerivedTripleg, displayIndex: number) => {
    event.stopPropagation();
    setEditingTripleg({ tripleg, displayIndex });
    setTriplegModeDraft(
      MANUAL_TRIPLEG_MODE_OPTIONS.some((option) => option.value === tripleg.effective_mode)
        ? tripleg.effective_mode
        : 'walk'
    );
  };

  const closeTriplegModeModal = () => {
    if (!savingTriplegMode) {
      setEditingTripleg(null);
    }
  };

  const saveTriplegMode = async () => {
    if (!editingTripleg) {
      return;
    }
    setSavingTriplegMode(true);
    try {
      await onOverrideTriplegMode(editingTripleg.tripleg.id, triplegModeDraft);
      setEditingTripleg(null);
    } finally {
      setSavingTriplegMode(false);
    }
  };

  return (
    <>
      <div className="location-analysis-timeline">
        {items.map((item) => {
          const target = { type: item.type, id: item.id } as DerivedTimelineTarget;
          const selected = isSameTimelineTarget(selectedTarget, target);
          if (item.type === 'staypoint') {
            const locationId = item.staypoint.effective_location_id;
            const location = locationId ? locations.find((row) => row.id === locationId) : undefined;
            const locationName = locationId ? locationNameMap.get(locationId) || `Location ${locationId.slice(0, 8)}` : null;
            const linkedPoi = locationId ? locationPoiMap.get(locationId) : null;
            const poiCandidates = locationId ? poiCandidatesByLocationId.get(locationId) || [] : [];
            const poiSearchOpened = editable && !!locationId && poiCandidatesByLocationId.has(locationId);
            const poiSearchQuery = locationId ? poiSearchQueryByLocationId.get(locationId) || '' : '';
            const poiQuerySearching = !!locationId && (
              querySearchingPoiLocationId === locationId || searchingPoiLocationId === locationId
            );
            const editing = editable && !!locationId && editingLocationId === locationId;
            const poiAddress = linkedPoi?.formatted_address?.trim();
            const startedAt = formatTimelineDateTime(item.started_at);
            const endedAt = formatTimelineDateTime(item.ended_at);
            const duration = formatTimelineDuration(item.started_at, item.ended_at);
            return (
              <div
                key={`staypoint-${item.id}`}
                role="button"
                tabIndex={0}
                className={`location-analysis-timeline-item ${selected ? 'location-analysis-timeline-item-selected' : ''}`}
                onClick={() => onSelect(target)}
                onKeyDown={(event) => handleTimelineKeyDown(event, target)}
              >
                <span className="location-analysis-timeline-rail">
                  <span className="location-analysis-timeline-icon location-analysis-timeline-icon-stay">
                    <SemanticTypeIcon semanticType={location?.semantic_type} />
                  </span>
                </span>
                <span className="location-analysis-timeline-content">
                  <span className="location-analysis-timeline-row">
                    {editing ? (
                      <Space.Compact onClick={(event) => event.stopPropagation()}>
                        <Input
                          size="small"
                          value={draftLocationLabel}
                          placeholder="Location 名"
                          onChange={(event) => setDraftLocationLabel(event.target.value)}
                          onKeyDown={(event) => {
                            event.stopPropagation();
                            if (event.key === 'Enter') {
                              void saveEditingLocation();
                            }
                            if (event.key === 'Escape') {
                              setEditingLocationId(null);
                              setDraftLocationLabel('');
                            }
                          }}
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={savingLocationId === locationId}
                          onClick={(event) => void saveEditingLocation(event)}
                        />
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          disabled={savingLocationId === locationId}
                          onClick={cancelEditingLocation}
                        />
                      </Space.Compact>
                    ) : (
                      <strong>{locationName || 'Location 未所属'}</strong>
                    )}
                    <Tag color="green" icon={<SemanticTypeIcon semanticType={location?.semantic_type} />}>
                      {getSemanticTypeLabel(location?.semantic_type)}
                    </Tag>
                    {!editing && editable ? (
                      <>
                        {locationId ? (
                          <Button
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            aria-label="Location 名を編集"
                            onClick={(event) => startEditingLocation(event, locationId, locationName)}
                          />
                        ) : null}
                        <Button
                          size="small"
                          onClick={(event) => openReassignModal(event, item.staypoint, item.displayIndex)}
                        >
                          ロケーション修正
                        </Button>
                        {locationId ? (
                          <Button
                            size="small"
                            icon={<SearchOutlined />}
                            loading={searchingPoiLocationId === locationId}
                            onClick={(event) => {
                              event.stopPropagation();
                              void onSearchLocationPoi(locationId, '');
                            }}
                          >
                            POI検索
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </span>
                  {poiAddress ? (
                    <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                      {poiAddress}
                    </span>
                  ) : null}
                  {poiSearchOpened && locationId ? (
                    <span className="location-analysis-poi-candidates" onClick={(event) => event.stopPropagation()}>
                      <span className="location-analysis-poi-candidates-header">
                        Google POI 候補 {poiCandidates.length > 0 ? `(${poiCandidates.length})` : '(0)'}
                      </span>
                      {poiCandidates.length > 0 ? (
                        poiCandidates.map((candidate) => (
                          <span
                            className="location-analysis-poi-candidate"
                            key={`${candidate.provider}:${candidate.provider_place_id || candidate.display_name}`}
                          >
                            <span>
                              <strong>{candidate.display_name || candidate.provider_place_id || '名称なし'}</strong>
                              <span className="location-analysis-timeline-secondary">
                                {candidate.formatted_address || '-'}
                                {typeof candidate.distance_m === 'number' ? ` / ${candidate.distance_m.toFixed(0)}m` : ''}
                              </span>
                            </span>
                            {editable ? (
                              <Button
                                size="small"
                                type="link"
                                loading={linkingPoiLocationId === locationId}
                                onClick={() => void onLinkLocationPoi(locationId, candidate)}
                              >
                                紐付け
                              </Button>
                            ) : null}
                          </span>
                        ))
                      ) : (
                        <span className="location-analysis-poi-empty">近傍候補はありません。施設名などで絞り込み検索できます。</span>
                      )}
                      <Space.Compact className="location-analysis-poi-query-search">
                        <Input
                          size="small"
                          allowClear
                          value={poiSearchQuery}
                          placeholder="2文字以上で自動検索"
                          onKeyDown={(event) => event.stopPropagation()}
                          onPressEnter={(event) => {
                            event.stopPropagation();
                            const normalizedValue = poiSearchQuery.trim();
                            if (!normalizedValue) {
                              message.warning('検索語を入力してください');
                              return;
                            }
                            void runLocationPoiQuerySearch(locationId, normalizedValue);
                          }}
                          onChange={(event) => {
                            const value = event.target.value;
                            setPoiSearchQueryByLocationId((current) => {
                              const next = new Map(current);
                              const previousValue = current.get(locationId) || '';
                              const normalizedPreviousValue = previousValue.trim();
                              const normalizedValue = value.trim();
                              const shouldIncrementalSearch =
                                normalizedValue.length > normalizedPreviousValue.length &&
                                normalizedValue.startsWith(normalizedPreviousValue);
                              incrementalPoiSearchAllowedRef.current.set(locationId, shouldIncrementalSearch);
                              if (shouldIncrementalSearch && normalizedValue.length >= POI_INCREMENTAL_SEARCH_MIN_QUERY_LENGTH) {
                                setQuerySearchingPoiLocationId(locationId);
                              } else if (querySearchingPoiLocationId === locationId && searchingPoiLocationId !== locationId) {
                                setQuerySearchingPoiLocationId(null);
                              }
                              if (value) {
                                next.set(locationId, value);
                              } else {
                                next.delete(locationId);
                                incrementalPoiSearchAllowedRef.current.delete(locationId);
                                lastIncrementalPoiQueryRef.current.delete(locationId);
                                if (querySearchingPoiLocationId === locationId) {
                                  setQuerySearchingPoiLocationId(null);
                                }
                              }
                              return next;
                            });
                          }}
                        />
                        <Button
                          size="small"
                          type="primary"
                          loading={poiQuerySearching}
                          onClick={(event) => {
                            event.stopPropagation();
                            const normalizedValue = poiSearchQuery.trim();
                            if (!normalizedValue) {
                              message.warning('検索語を入力してください');
                              return;
                            }
                            void runLocationPoiQuerySearch(locationId, normalizedValue);
                          }}
                        >
                          {poiQuerySearching ? '検索中' : 'クエリ検索'}
                        </Button>
                      </Space.Compact>
                    </span>
                  ) : null}
                  <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                    <strong>{duration}</strong>
                    <span>({startedAt} - {endedAt})</span>
                  </span>
                  <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                    SP{item.displayIndex}
                  </span>
                  <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                    {item.staypoint.centroid_latitude.toFixed(6)}, {item.staypoint.centroid_longitude.toFixed(6)}
                  </span>
                </span>
              </div>
            );
          }

          const startedAt = formatTimelineDateTime(item.started_at);
          const endedAt = formatTimelineDateTime(item.ended_at);
          const duration = formatTimelineDuration(item.started_at, item.ended_at);
          const distance = formatTimelineDistance(item.tripleg.matched_distance_m);
          const averageSpeed = formatAverageSpeed(item.tripleg.matched_distance_m, item.started_at, item.ended_at);

          return (
            <div
              key={`tripleg-${item.id}`}
              role="button"
              tabIndex={0}
              className={`location-analysis-timeline-item ${selected ? 'location-analysis-timeline-item-selected' : ''}`}
              onClick={() => onSelect(target)}
              onKeyDown={(event) => handleTimelineKeyDown(event, target)}
            >
              <span className="location-analysis-timeline-rail">
                <span className="location-analysis-timeline-icon location-analysis-timeline-icon-tripleg">
                  <TriplegModeIcon mode={item.tripleg.effective_mode} />
                </span>
              </span>
              <span className="location-analysis-timeline-content">
                <span className="location-analysis-timeline-row">
                  <strong>{getTriplegModeLabel(item.tripleg.effective_mode)}</strong>
                  <span className="location-analysis-timeline-secondary">auto: {item.tripleg.inferred_mode}</span>
                  {editable ? (
                    <>
                      <Button size="small" onClick={(event) => openTriplegModeModal(event, item.tripleg, item.displayIndex)}>
                        移動手段修正
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={(event) => {
                          event.stopPropagation();
                          Modal.confirm({
                            title: `Tripleg ${item.displayIndex} を削除しますか？`,
                            content: '元の自動推定結果は保持し、手動削除 override としてタイムラインから非表示にします。',
                            okText: '削除',
                            okButtonProps: { danger: true },
                            cancelText: 'キャンセル',
                            onOk: () => onDeleteTripleg(item.tripleg.id),
                          });
                        }}
                      >
                        削除
                      </Button>
                    </>
                  ) : null}
                </span>
                <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                  {distance}（平均速度 {averageSpeed}）
                </span>
                <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                  <strong>{duration}</strong>
                  <span>({startedAt} - {endedAt})</span>
                </span>
                <span className="location-analysis-timeline-row location-analysis-timeline-secondary">
                  測定点数：{item.tripleg.source_snapshot_count} 点
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <Modal
        title={reassignTarget ? `SP ${reassignTarget.displayIndex} のロケーション修正` : 'ロケーション修正'}
        open={editable && !!reassignTarget}
        onCancel={closeReassignModal}
        onOk={() => void saveReassign()}
        confirmLoading={savingReassign}
        okText="保存"
        cancelText="キャンセル"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            現在: {reassignTarget?.staypoint.effective_location_id
              ? locationNameMap.get(reassignTarget.staypoint.effective_location_id) ||
                `Location ${reassignTarget.staypoint.effective_location_id.slice(0, 8)}`
              : 'Location 未所属'}
          </Text>
          <Select
            value={reassignMode}
            style={{ width: '100%' }}
            onChange={(value) => setReassignMode(value as 'assign' | 'create_location' | 'clear')}
            options={[
              { label: '既存 location に割り当てる', value: 'assign' },
              { label: 'この staypoint から新規 location を作る', value: 'create_location' },
              { label: '自動推定に戻す', value: 'clear' },
            ]}
          />
          {reassignMode === 'assign' ? (
            <>
              <Select
                showSearch
                value={reassignLocationId}
                placeholder="割当先 location"
                style={{ width: '100%' }}
                optionFilterProp="label"
                onChange={(value) => {
                  setReassignLocationId(value);
                  const selectedLocation = locations.find((location) => location.id === value);
                  setReassignSemanticType(selectedLocation?.semantic_type || 'unknown');
                }}
                options={locations
                  .filter((location) => location.status !== 'merged')
                  .map((location) => ({
                    label: `${location.label?.trim() || `Location ${location.id.slice(0, 8)}`} (${getSemanticTypeLabel(location.semantic_type)})`,
                    value: location.id,
                  }))}
              />
              <Select
                value={reassignSemanticType}
                style={{ width: '100%' }}
                onChange={(value) => setReassignSemanticType(value)}
                options={SEMANTIC_TYPE_OPTIONS.map((value) => ({ label: getSemanticTypeLabel(value), value }))}
              />
            </>
          ) : null}
          {reassignMode === 'create_location' ? (
            <>
              <Input
                value={newLocationLabel}
                placeholder="新規 location ラベル（任意）"
                onChange={(event) => setNewLocationLabel(event.target.value)}
              />
              <Select
                value={newLocationSemanticType}
                style={{ width: '100%' }}
                onChange={(value) => setNewLocationSemanticType(value)}
                options={SEMANTIC_TYPE_OPTIONS.map((value) => ({ label: getSemanticTypeLabel(value), value }))}
              />
            </>
          ) : null}
          {reassignMode === 'clear' ? (
            <Alert
              type="info"
              showIcon
              message="現在の staypoint override を解除し、trackintel 由来の location 割当に戻します。"
            />
          ) : null}
        </Space>
      </Modal>
      <Modal
        title={editingTripleg ? `Tripleg ${editingTripleg.displayIndex} の移動手段修正` : '移動手段修正'}
        open={editable && !!editingTripleg}
        onCancel={closeTriplegModeModal}
        onOk={() => void saveTriplegMode()}
        confirmLoading={savingTriplegMode}
        okText="保存"
        cancelText="キャンセル"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            自動推定: {editingTripleg ? getTriplegModeLabel(editingTripleg.tripleg.inferred_mode) : '-'}
          </Text>
          <Select
            value={triplegModeDraft}
            style={{ width: '100%' }}
            onChange={(value) => setTriplegModeDraft(value)}
            options={MANUAL_TRIPLEG_MODE_OPTIONS.map((option) => ({
              ...option,
              label: (
                <Space>
                  <TriplegModeIcon mode={option.value} />
                  <span>{option.label}</span>
                </Space>
              ),
            }))}
          />
        </Space>
      </Modal>
    </>
  );
};

export default LocationAnalysisTimeline;
